import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../LanguageContext";
import { Chapter, Reveal } from "./Chapter";

const About = () => {
  const { t } = useLang();
  const a = t.about;
  const c = a.counseling;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  return (
    <Chapter id="chi-sono" num={a.num} kicker={a.kicker} title={a.title}>
      <div className="grid gap-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative max-w-[55%] min-w-[210px]">
              <div className="absolute -left-4 -top-4 h-24 w-24 border-l border-t border-terra" />
              <motion.div whileHover={{ scale: 0.985 }} transition={{ duration: 0.5 }} className="overflow-hidden">
                <img
                  src="/images/eros-portrait.jpg"
                  alt="Eros Zannoni"
                  data-testid="about-portrait"
                  className="aspect-[4/5] w-full object-cover"
                />
              </motion.div>
              <div
                className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(70% 45% at 50% 0%, rgba(212,163,89,0.16), transparent 70%)" }}
              />
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-moss">
                Eros Zannoni — Naturopata Olistico
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="mt-14 font-serif text-2xl font-light tracking-tight sm:text-3xl">{a.credentialsTitle}</h3>
            <ul className="mt-6 space-y-4">
              {a.credentials.map((c, i) => (
                <li key={i} className="flex gap-3 text-sm font-light leading-relaxed text-moss">
                  <span className="mt-2 h-1 w-4 shrink-0 bg-terra" />
                  {c}
                </li>
              ))}
            </ul>
            <div data-testid="counseling-box" className="mt-10 rounded-2xl bg-forest p-7 text-linen shadow-lg shadow-forest/20 sm:p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{c.boxKicker}</p>
              <h4 className="mt-3 font-serif text-2xl font-light leading-snug tracking-tight">{c.boxTitle}</h4>
              <p className="mt-4 text-sm font-light leading-relaxed text-linen/80">{c.boxP1}</p>
              <h5 className="mt-6 font-serif text-lg font-light italic text-gold">{c.boxSub2}</h5>
              <p className="mt-2 text-sm font-light leading-relaxed text-linen/80">{c.boxP2}</p>
              <p className="mt-5 border-l-2 border-terra pl-4 text-sm font-light italic leading-relaxed text-linen/90">{c.boxP3}</p>
              <button
                data-testid="counseling-article-open"
                onClick={() => setOpen(true)}
                className="mt-6 rounded-full border border-linen/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-linen transition-colors duration-300 hover:bg-terra hover:border-terra"
              >
                {c.cta}
              </button>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <p className="text-lg font-light leading-relaxed text-ink sm:text-xl">{a.intro}</p>
            <p className="mt-6 text-base font-light leading-relaxed text-moss">{a.body}</p>
          </Reveal>

          <h3 className="sr-only">{a.bioTitle}</h3>
          <div className="mt-16 space-y-0 border-t border-ink/10">
            {a.bio.map((item, i) => (
              <Reveal key={i} delay={Math.min(i * 0.04, 0.3)} y={20}>
                <div className="group flex gap-3 border-b border-ink/10 py-4 transition-colors duration-300 hover:bg-sand/60 sm:gap-4">
                  <span className="mt-2 h-1 w-4 shrink-0 bg-terra" />
                  <p className="text-sm font-light leading-relaxed text-ink/85 sm:text-base">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            key="counseling-modal"
            data-testid="counseling-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[95] flex items-end justify-center bg-forest/70 p-0 backdrop-blur-md sm:items-center sm:p-8"
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto bg-linen sm:max-h-[85vh]"
            >
              <div className="sticky top-0 z-10 flex items-start justify-between gap-6 border-b border-ink/10 bg-linen/95 p-6 backdrop-blur sm:p-8">
                <h3 className="font-serif text-2xl font-light tracking-tight text-forest sm:text-3xl">{c.modalTitle}</h3>
                <button
                  data-testid="counseling-modal-close"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="shrink-0 rounded-full border border-ink/15 p-2.5 text-forest transition-colors duration-300 hover:bg-terra hover:text-linen"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="space-y-10 p-6 sm:p-10">
                {c.article.map((s, i) => (
                  <div key={i}>
                    <h4 className="flex items-baseline gap-3 font-serif text-xl font-normal text-terra sm:text-2xl">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-moss">{String(i + 1).padStart(2, "0")}</span>
                      {s.h}
                    </h4>
                    <div className="mt-4 space-y-4">
                      {s.blocks.map((b, j) =>
                        b.list ? (
                          <ul key={j} className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                            {b.list.map((li, k) => (
                              <li key={k} className="flex gap-2.5 text-sm font-light leading-relaxed text-moss">
                                <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-terra" />
                                {li}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p key={j} className="text-sm font-light leading-relaxed text-ink/85 sm:text-base">{b.p}</p>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Chapter>
  );
};

export default About;
